import type { GoogleCredential } from '@zuruflex/api-client';
import type { StaffSession } from '@zuruflex/types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { session as zuruflexSession } from './api.js';

interface AuthState {
  user: StaffSession['user'] | null;
  /** True until the stored refresh token has been tried, once, on boot. */
  isRestoring: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (credential: GoogleCredential) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Holds the signed-in staff member.
 *
 * The session itself lives outside React, in `lib/api`, because the API
 * client needs the token on requests that fire before — and after — any
 * component is mounted. This only mirrors it into render.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<StaffSession['user'] | null>(
    () => (zuruflexSession.current as StaffSession | null)?.user ?? null,
  );
  const [isRestoring, setIsRestoring] = useState(zuruflexSession.canRestore);

  useEffect(() => {
    // Keeps render in step with refreshes and revocations the client does
    // on its own, which no component asked for and none would otherwise see.
    const unsubscribe = zuruflexSession.subscribe((next) =>
      setUser((next as StaffSession | null)?.user ?? null),
    );
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!zuruflexSession.canRestore) return;
    let cancelled = false;
    void zuruflexSession.restore().finally(() => {
      if (!cancelled) setIsRestoring(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    await zuruflexSession.signInWithPassword(email, password);
  }, []);

  const signInWithGoogle = useCallback(async (credential: GoogleCredential) => {
    await zuruflexSession.signInWithGoogle(credential);
  }, []);

  const signOut = useCallback(async () => {
    await zuruflexSession.signOut();
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, isRestoring, signIn, signInWithGoogle, signOut }),
    [user, isRestoring, signIn, signInWithGoogle, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
